import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for dynamic meta tags
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.keywords - SEO keywords
 * @param {string} props.canonical - Canonical URL
 * @param {string} props.ogType - Open Graph type (default: 'website')
 * @param {string} props.ogImage - Open Graph image URL (WebP preferred)
 * @param {string} props.ogImagePng - Fallback PNG image URL
 * @param {boolean} props.includeHreflang - Include hreflang tags for multilingual pages
 * @param {string} props.pagePath - Path to page (e.g., '/blog') for hreflang generation
 */
const SEO = ({
    title = 'PPE SCOPE - Professional Personality & Emotion Assessment',
    description = 'Comprehensive personality assessment tool for professional development. Evaluate personality traits, emotional intelligence, coping strategies, and work preferences with PPE SCOPE.',
    keywords = 'personality assessment, emotional intelligence, professional development, psychometric testing, workplace assessment, personality test, coping strategies, work preferences',
    canonical = 'https://ppe-scope.ch/',
    ogType = 'website',
    ogImage = 'https://ppe-scope.ch/assets/logos/logo512green.png',
    ogImagePng = null,
    includeHreflang = false,
    pagePath = ''
}) => {
    const languages = ['en', 'fr', 'de', 'it', 'es'];
    const baseUrl = 'https://ppe-scope.ch';
    // Derive current language from canonical URL when available (fallback to 'en')
    let selfLang = 'en';
    try {
        const urlObj = new URL(canonical);
        const pathMatch = urlObj.pathname.match(/^\/(en|fr|de|it|es)(?:\/|$)/i);
        if (pathMatch) selfLang = pathMatch[1].toLowerCase();
    } catch (e) {
        // ignore invalid URL and keep default
    }
    
    return (
        <Helmet>
            {/* Override main page meta tags */}
            <meta name="title" content={title} />
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <link rel="canonical" href={canonical} />
            
            {/* Primary Meta Tags */}
            <title>{title}</title>

            {/* Hreflang tags for multilingual SEO */}
            {includeHreflang && languages.map(lang => (
                <link
                    key={lang}
                    rel="alternate"
                    hrefLang={lang}
                    href={`${baseUrl}/${lang}${pagePath}`}
                />
            ))}
            {includeHreflang && (
                <link
                    rel="alternate"
                    hrefLang="x-default"
                    href={`${baseUrl}/en${pagePath}`}
                />
            )}
            {/* Self-referential alternate: only emit if it doesn't duplicate the language-specific alternate */}
            {includeHreflang && canonical !== `${baseUrl}/${selfLang}${pagePath}` && (
                <link
                    rel="alternate"
                    hrefLang={selfLang}
                    href={canonical}
                />
            )}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:type" content={ogImage.endsWith('.webp') ? 'image/webp' : 'image/png'} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            
            {/* PNG fallback for platforms that don't support WebP */}
            {ogImagePng && (
                <>
                    <meta property="og:image" content={ogImagePng} />
                    <meta property="og:image:type" content="image/png" />
                    <meta property="og:image:width" content="1200" />
                    <meta property="og:image:height" content="630" />
                </>
            )}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonical} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />
        </Helmet>
    );
};

export default SEO;
